import { PictureOutlined } from '@ant-design/icons'
import AttachmentEditor from '@components/AttachmentEditor'
import HasAccess, { useHasAccess } from '@components/HasAccess'
import { GalleryItem as GalleryItemModel } from '@models/GalleryItem'
import { DndGrid } from '@pages/venues/detail/tabs/DndGrid'
import {
  createVenuePicture,
  deleteVenuePicture,
  fetchVenueGallery,
  reorderVenueGallery,
  updateFeaturedVenuePicture,
} from '@services/VenueGalleryService'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Permission } from '@utils/permissions'
import { requiredRule } from '@utils/rules'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { GalleryItem } from './Item'

const { Title } = Typography
const GalleryCard = () => {
  const [form] = Form.useForm()
  const venue = useAppSelector(selectSelectedVenue())
  const [gallery, setGallery] = useState<GalleryItemModel[]>([])
  const [addModalIsOpen, setAddModalIsOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { hasAccess } = useHasAccess()

  const updateFeaturedPicture = (item: GalleryItemModel) => {
    if (!venue) return <></>
    setIsLoading(true)
    updateFeaturedVenuePicture(venue.slug, {
      ...item,
      isFeatured: true,
    })
      .then(() => fetchGallery())
      .finally(() => setIsLoading(false))
  }

  const query: TableParams = {
    ...initialQuery,
    columnKey: 'order',
    order: 'ascend',
    pagination: { ...initialQuery.pagination, pageSize: 1000 },
  }

  const fetchGallery = () => {
    if (!venue) return
    setIsLoading(true)
    fetchVenueGallery(venue.slug, query)
      .then((data) => {
        setGallery(data.data)
      })
      .finally(() => setIsLoading(false))
  }

  const onFormModalSubmited = (values: any) => {
    if (!venue) return
    setIsLoading(true)
    const data = new FormData()
    values.file.forEach((element: any, index: number) => {
      data.append(`files[]`, element.originFileObj)
    })
    createVenuePicture(venue.slug, data)
      .then(() => {
        fetchGallery()
        form.resetFields()
        setAddModalIsOpen(false)
      })
      .finally(() => setIsLoading(false))
  }

  const onOrderChanged = (updatedGallery: GalleryItemModel[]) => {
    if (!venue) return
    const nextOrderData = updatedGallery.map((m, i) => ({
      id: m.id,
      order: i,
    }))
    reorderVenueGallery(venue.slug, nextOrderData)
  }

  useEffect(() => {
    fetchGallery()
  }, [venue])

  if (!venue) return <></>

  return (
    <>
      <Flex vertical className={'w-full'} gap={8}>
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            <PictureOutlined /> Gallery
          </Title>
          <Space>
            <HasAccess permissions={[Permission.updateVenue]}>
              <Button
                onClick={() => setAddModalIsOpen(true)}
                style={{ border: 0 }}
                type="primary"
                loading={isLoading}
              >
                Add new
              </Button>
            </HasAccess>
          </Space>
        </Space>

        <Card styles={{ body: { padding: 16, paddingBottom: 10 } }}>
          <Row gutter={[16, 16]}>
            <DndGrid<GalleryItemModel>
              items={gallery}
              styles={{
                idle: {
                  cursor: hasAccess(Permission.updateVenue)
                    ? 'move'
                    : 'default',
                },
                dragging: { opacity: 0.4 },
                over: { opacity: 0.4 },
              }}
              onItemsChange={onOrderChanged}
              renderItem={(item, style, ref) => (
                <Col span={8} ref={ref}>
                  <GalleryItem
                    picture={item}
                    style={style}
                    onSetFeaturedClicked={() => updateFeaturedPicture(item)}
                    onDeleteClicked={() =>
                      deleteVenuePicture(venue.slug, item).then(() =>
                        fetchGallery()
                      )
                    }
                  />
                </Col>
              )}
            />
          </Row>
        </Card>
      </Flex>
      <Modal
        open={addModalIsOpen}
        onCancel={() => setAddModalIsOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        okText="Submit"
      >
        <Form
          form={form}
          layout="vertical"
          name="attachment-editor"
          onFinish={onFormModalSubmited}
        >
          <AttachmentEditor
            formItem={{ name: 'file', rules: [requiredRule()] }}
            dragger={{ fileTypes: ['picture'], multiple: true }}
            image={{ width: 1600, height: 900, aspect: 16 / 9 }}
          />
        </Form>
      </Modal>
    </>
  )
}

type GalleryCardType = { Card: typeof GalleryCard }

export { GalleryCard, type GalleryCardType }
